import { type JSX, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { TPRM_ROLES } from 'Helpers/UserUtils';
import { ScheduleV2, ServiceForNonTprmUser, ThirdPartyResponseWithServices, ThirdPartyService, ThirdPartyWithServicesForNonTprmUser } from 'Models/TPRM';

import { ManageThirdPartyServiceForm } from './Components/ManageThirdPartyServiceForm/ManageThirdPartyServiceForm';

export interface ManageThirdPartyServiceProps {
    authHandler: AuthHandler;
    tprmApi: ClientTPRMApi;
}

export const ManageThirdPartyService = (props: ManageThirdPartyServiceProps): JSX.Element => {
    const { thirdPartyId, serviceId } = useParams();
    const serviceIdExists = thirdPartyId !== undefined && serviceId !== undefined;

    const userRoles = props.authHandler.getUserRoles();
    const userHasFullTprmAccess = userHasAuthorizedRole(userRoles, TPRM_ROLES);

    if (serviceIdExists) {
        if (userHasFullTprmAccess) {
            return <UserWithFullTprmAccessUpdatingService thirdPartyId={thirdPartyId} serviceId={serviceId} tprmApi={props.tprmApi} />;
        } else {
            return <UserWithLimitedTprmAccessUpdatingService thirdPartyId={thirdPartyId} serviceId={serviceId} tprmApi={props.tprmApi} />;
        }
    } else {
        if (userHasFullTprmAccess) {
            return <UserWithFullTprmAccessCreatingService tprmApi={props.tprmApi} />;
        } else {
            return <UserWithLimitedTprmAccessCreatingService tprmApi={props.tprmApi} />;
        }
    }
};

const UserWithLimitedTprmAccessCreatingService = (props: { tprmApi: ClientTPRMApi }): JSX.Element => {
    const [thirdParties, setThirdParties] = useState<ThirdPartyWithServicesForNonTprmUser[]>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);

    useEffect(() => {
        const getThirdParties = async (): Promise<void> => {
            try {
                setThirdParties((await props.tprmApi.getThirdPartiesForNonTprmUser()).data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        getThirdParties();
    }, [props.tprmApi]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (thirdParties === undefined) {
        return <Placeholder />;
    } else {
        return <ManageThirdPartyServiceForm userHasFullTprmAccess={false} serviceExists={false} thirdParties={thirdParties} tprmApi={props.tprmApi} />;
    }
};

const UserWithLimitedTprmAccessUpdatingService = (props: { thirdPartyId: string; serviceId: string; tprmApi: ClientTPRMApi }): JSX.Element => {
    const [service, setService] = useState<ServiceForNonTprmUser>();
    const [thirdParties, setThirdParties] = useState<ThirdPartyWithServicesForNonTprmUser[]>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);

    useEffect(() => {
        const getThirdParties = async (): Promise<void> => {
            try {
                setThirdParties((await props.tprmApi.getThirdPartiesForNonTprmUser()).data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        const getThirdPartyService = async (): Promise<void> => {
            try {
                setService((await props.tprmApi.getServiceDetailsForNonTprmUser(props.thirdPartyId, props.serviceId)).data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        getThirdParties();
        getThirdPartyService();
    }, [props.serviceId, props.thirdPartyId, props.tprmApi]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (service === undefined || thirdParties === undefined) {
        return <Placeholder />;
    } else {
        return <ManageThirdPartyServiceForm userHasFullTprmAccess={false} serviceExists={true} service={service} thirdParties={thirdParties} tprmApi={props.tprmApi} />;
    }
};

const UserWithFullTprmAccessCreatingService = (props: { tprmApi: ClientTPRMApi }): JSX.Element => {
    const [thirdParties, setThirdParties] = useState<ThirdPartyResponseWithServices[]>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);

    // This state will contain `null` if the request for data has completed, but no data exists.
    const [irqTargetCompletionSetting, setIrqTargetCompletionSetting] = useState<ScheduleV2 | null>();

    useEffect(() => {
        const getThirdParties = async (): Promise<void> => {
            try {
                setThirdParties((await props.tprmApi.getThirdParties()).data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        const getInherentRiskQuestionnaireTargetCompletion = async (): Promise<void> => {
            try {
                const response = await props.tprmApi.getInherentRiskQuestionnaireTargetCompletion();
                setIrqTargetCompletionSetting(response.data ?? null);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        getThirdParties();
        getInherentRiskQuestionnaireTargetCompletion();
    }, [props.tprmApi]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (thirdParties === undefined || irqTargetCompletionSetting === undefined) {
        return <Placeholder />;
    } else {
        return <ManageThirdPartyServiceForm userHasFullTprmAccess={true} irqTargetCompletionSetting={irqTargetCompletionSetting ?? undefined} serviceExists={false} thirdParties={thirdParties} tprmApi={props.tprmApi} />;
    }
};

const UserWithFullTprmAccessUpdatingService = (props: { thirdPartyId: string; serviceId: string; tprmApi: ClientTPRMApi }): JSX.Element => {
    const [service, setService] = useState<ThirdPartyService>();
    const [thirdParties, setThirdParties] = useState<ThirdPartyResponseWithServices[]>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);

    // This state will contain `null` if the request for data has completed, but no data exists.
    const [irqTargetCompletionSetting, setIrqTargetCompletionSetting] = useState<ScheduleV2 | null>();

    useEffect(() => {
        const getThirdParties = async (): Promise<void> => {
            try {
                setThirdParties((await props.tprmApi.getThirdParties()).data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        const getThirdPartyService = async (): Promise<void> => {
            try {
                setService((await props.tprmApi.getServiceDetails(props.thirdPartyId, props.serviceId)).data);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        const getInherentRiskQuestionnaireTargetCompletion = async (): Promise<void> => {
            try {
                const response = await props.tprmApi.getInherentRiskQuestionnaireTargetCompletion();
                setIrqTargetCompletionSetting(response.data ?? null);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };

        getThirdParties();
        getThirdPartyService();
        getInherentRiskQuestionnaireTargetCompletion();
    }, [props.serviceId, props.thirdPartyId, props.tprmApi]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (service === undefined || thirdParties === undefined || irqTargetCompletionSetting === undefined) {
        return <Placeholder />;
    } else {
        return <ManageThirdPartyServiceForm userHasFullTprmAccess={true} serviceExists={true} irqTargetCompletionSetting={irqTargetCompletionSetting ?? undefined} service={service} thirdParties={thirdParties} tprmApi={props.tprmApi} />;
    }
};
